import Button from '@carvana/ds-button';

type TourStop = {
  n: number;
  tag: string;
  aspect: string;
  title: string;
  stopColor: string;
  car: {
    model?: string;
    trim: string;
    color: string;
    stall: { id: string; walk: string };
  };
};

type OwnProps = {
  buildSummary: {
    make: string;
    model: string;
    year: number;
    trim: string;
    color?: string;
    interior?: string;
  };
  tourData: {
    stops: TourStop[];
    notOnLot?: string[];
  } | null;
  onStartOver: () => void;
  onEndSession: () => void;
};

const CORE_TAGS = new Set([
  'trim-match',
  'color-match',
  'interior-match',
  'option-match',
  'best-available'
]);

export default function TdcSentScreen({
  buildSummary,
  tourData,
  onStartOver,
  onEndSession
}: OwnProps) {
  const stops = tourData?.stops || [];
  const core = stops.filter(s => CORE_TAGS.has(s.tag));
  const notOnLot = tourData?.notOnLot || [];

  return (
    <div
      className="mx-auto max-w-[600px] px-lg py-2xl"
      data-testid="tdc-sent-screen"
    >
      <div className="mb-xl flex items-center gap-lg">
        <div className="flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-interactive-default text-2xl text-text-inverse">
          ✓
        </div>
        <div>
          <h2 className="t-heading-xl">Tour ready</h2>
          <p className="t-body-sm text-text-weak">
            Follow the stops on the big screen.
          </p>
        </div>
      </div>

      <div className="mb-xl rounded-xl border border-border-default bg-background-muted p-lg">
        <p className="t-label-xs mb-sm uppercase tracking-wider text-text-weakest">
          Your Build
        </p>
        <div className="flex flex-wrap gap-sm">
          {[
            `${buildSummary.make} ${buildSummary.model}`,
            buildSummary.trim,
            buildSummary.color,
            buildSummary.interior
          ]
            .filter(Boolean)
            .map((item, i) => (
              <span
                key={i}
                className="rounded-full border border-border-default bg-background-default px-md py-xs text-sm font-semibold"
              >
                {item}
              </span>
            ))}
        </div>
      </div>

      {core.length > 0 && (
        <>
          <p className="t-label-xs mb-md uppercase tracking-wider text-text-weakest">
            Your recommended stops
          </p>
          <div className="mb-xl flex flex-col gap-sm">
            {core.map(stop => (
              <div
                key={stop.n}
                className="flex items-center gap-lg rounded-xl border border-border-default bg-background-default p-lg"
              >
                <div
                  className="flex h-[40px] w-[40px] shrink-0 items-center justify-center rounded-full text-lg font-black"
                  style={{
                    backgroundColor: stop.stopColor,
                    color: '#04101a'
                  }}
                >
                  {stop.n}
                </div>
                <div>
                  <p className="t-strong-md">{stop.title}</p>
                  <p className="t-body-sm text-text-weak">
                    {stop.car.stall.id} · {stop.car.trim} · {stop.aspect}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {notOnLot.length > 0 && (
        <p className="mb-xl border-l-[3px] border-border-default py-xs pl-md text-sm text-text-weakest">
          Not on display: {notOnLot.join(' · ')}
        </p>
      )}

      <div className="flex flex-col gap-md sm:flex-row">
        <Button
          className="flex-1"
          data-analytics-id="New Cars TDC"
          data-testid="tdc-start-over"
          onClick={onStartOver}
          size="md"
          variant="primary"
        >
          Start Over
        </Button>
        <Button
          className="flex-1 opacity-60"
          data-analytics-id="New Cars TDC"
          data-testid="tdc-end-session"
          onClick={onEndSession}
          size="md"
          variant="secondary"
        >
          End Session
        </Button>
      </div>
    </div>
  );
}
